import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Container } from 'components/base';
import { CloudinaryPicture } from 'components/rich';

import { Head, Layout } from '../components/layout';
import PdpIcon from '../components/product/pdpIcon';

const QUOTE_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSeCyW5n4I2Jv4qOVaf0ZRVUuDpW_PI39B8TPV3p6NBgdFbm5Q/viewform';

const PROOF_POINTS = [
  { icon: 'layers', label: '10+ of one product' },
  { icon: 'badgePercent', label: 'Bulk discount' },
  { icon: 'mailCheck', label: 'Personal quote' },
];

// Decorative, hidden on phones. Lazy so phones never fetch them.
const COLLAGE = [
  { imageKey: 'instagram/instagram2' },
  { imageKey: 'hero/wholesale' },
  { imageKey: 'classic-prints/3', prefix: 'products/' },
  { imageKey: 'categories/wallart' },
];

// Quantities are illustrative, the footnote says so.
const USE_CASES = [
  {
    who: 'The artist',
    description: 'Prints of your artwork or photography to sell at markets, in shops or online.',
    quantity: '×50',
    unit: 'Prints',
    imageKey: 'artprint/1',
  },
  {
    who: 'The company',
    description: 'Client and team gifts with one design, printed for everyone on the list.',
    quantity: '×75',
    unit: 'Calendars',
    imageKey: 'desktop-calendar/4',
  },
  {
    who: 'The family',
    description: 'The same album for every branch of the family, so nobody has to share.',
    quantity: '×12',
    unit: 'Photo books',
    imageKey: 'softcover-books/1',
  },
];

const STACK_COPIES = 4;

const QuoteButton = ({ className }) => (
  <a className={classNames('wsl-btn', className)} href={QUOTE_URL}>
    Request a quote
    <PdpIcon name="arrowRight" size={18} />
  </a>
);

QuoteButton.propTypes = {
  className: PropTypes.string,
};

const PrintStack = ({ imageKey, quantity, unit }) => (
  <div className="wsl-stack">
    {Array.from({ length: STACK_COPIES }, (_, index) => {
      const isTop = index === STACK_COPIES - 1;
      return (
        <div key={index} className="wsl-copy" aria-hidden={isTop ? undefined : true}>
          <CloudinaryPicture
            prefix="products/"
            imageKey={imageKey}
            alt={isTop ? undefined : ''}
            loading="lazy"
          />
        </div>
      );
    })}
    <p className="wsl-qty">
      <span className="wsl-qty-num">{quantity}</span>
      <span className="wsl-qty-unit">{unit}</span>
    </p>
  </div>
);

PrintStack.propTypes = {
  imageKey: PropTypes.string.isRequired,
  quantity: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
};

const WholesalePage = () => (
  <>
    <Head
      title="Wholesale & Bulk Photo Printing"
      description="Order photo prints, books and wall art in bulk. Parabo Press offers wholesale discounts on orders of 10 or more of the same product. Request a quote."
    />

    <Layout>
      <div className="wsl">
        <section className="wsl-hero">
          <Container className="wsl-hero-inner">
            <div className="wsl-hero-copy">
              {/* Keeps "Wholesale" in the H1, the display line below is not a heading. */}
              <h1 className="wsl-eyebrow">Wholesale & Bulk Orders</h1>
              <p className="wsl-h1">
                Print 10 or more.{' '}
                <br />
                Pay less for every copy.
              </p>
              <p className="wsl-lead">
                Selling your artwork, gifting a whole team or printing photo books for the entire
                family? Order 10 or more of the same product and we’ll send you a custom quote with
                bulk pricing.
              </p>
              <QuoteButton className="wsl-btn--navy" />
              <ul className="wsl-proof">
                {PROOF_POINTS.map(({ icon, label }) => (
                  <li key={label}>
                    <PdpIcon name={icon} size={18} />
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="wsl-collage" aria-hidden="true">
              {COLLAGE.map(({ imageKey, prefix }, index) => (
                <div key={imageKey} className={`wsl-print wsl-print--${index + 1}`}>
                  <CloudinaryPicture prefix={prefix} imageKey={imageKey} alt="" loading="lazy" />
                </div>
              ))}
              <p className="wsl-badge">
                <span className="wsl-badge-num">10+</span>
                <span className="wsl-badge-label">Same item</span>
              </p>
            </div>
          </Container>
        </section>

        <section className="wsl-who">
          <Container>
            <div className="wsl-who-head">
              <div>
                <p className="wsl-eyebrow wsl-eyebrow--ochre">Who orders in bulk</p>
                <h2 className="wsl-h2">
                  One design.
                  <br />
                  Many copies.
                </h2>
              </div>
              <p className="wsl-who-aside">
                If it’s 10 or more of the same product, it counts as a bulk order. A few of the
                orders we see most.
              </p>
            </div>

            <ul className="wsl-cases">
              {USE_CASES.map(({ who, description, ...stack }) => (
                <li key={who} className="wsl-case">
                  <PrintStack {...stack} />
                  <div className="wsl-case-text">
                    <h3 className="wsl-case-who">{who}</h3>
                    <p className="wsl-case-desc">{description}</p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="wsl-footnote">
              Quantities are examples. Any product works once you order 10 or more of it.
            </p>
          </Container>
        </section>

        <section className="wsl-cta">
          <Container>
            <div className="wsl-cta-band">
              <div className="wsl-cta-copy">
                <h2 className="wsl-cta-title">Planning an order of 10 or more?</h2>
                <p className="wsl-cta-sub">
                  Tell us what you’re printing and we’ll get back to you with a quote.
                </p>
              </div>
              <QuoteButton className="wsl-btn--white" />
            </div>
          </Container>
        </section>
      </div>
    </Layout>
  </>
);

export default WholesalePage;
